import { type CouponInput } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { LoggerService } from "@modules/common/logger/loggerService"
import { type PauseInputDto } from "@modules/common/pauseObject/gateway/pauseApiDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { type ProgramBoxSubscriberApiGateway } from "@modules/programs/programSubscriber/gateway/programBoxSubscriberApiGateway"
import {
  type ProgramSubscriberDto,
  ProgramSubscriberDtoSchema,
} from "@modules/programs/programSubscriber/gateway/programSubscriberDto"
import { wrapperToPage, type Page, type PaginatedWrapper } from "@modules/utils/paginated"

export class ProgramBoxSubscriberApi implements ProgramBoxSubscriberApiGateway {
  private buildBaseUrl = (programId: string) => `/programs/${programId}/subscribers`
  private buildUrl = (programId: string) => `${this.buildBaseUrl(programId)}/box`

  constructor(private api: ApiClient) {}

  async getProgramSubscriber(programId: string, boxId: string) {
    const result = await this.api.get<ProgramSubscriberDto>(`${this.buildUrl(programId)}/${boxId}`)
    return LoggerService.parseApiDto(ProgramSubscriberDtoSchema, result.data)
  }

  async updateSubscriberPause(programId: string, boxId: string, pause: PauseInputDto) {
    await this.api.post(`${this.buildUrl(programId)}/${boxId}/pause`, pause)
  }

  async resumeSubscription(programId: string, boxId: string) {
    await this.api.post(`${this.buildUrl(programId)}/${boxId}/resume`)
  }

  async updateSubscriberInternalNote(programId: string, boxId: string, note: string) {
    const result = await this.api.post(`${this.buildUrl(programId)}/${boxId}/note`, {
      note,
    })

    return LoggerService.parseApiDto(ProgramSubscriberDtoSchema, result.data)
  }

  async removeSubscriber(programId: string, boxId: string, cancelAtPeriodEnd: boolean) {
    await this.api.delete(`${this.buildUrl(programId)}/${boxId}`, {
      data: {
        cancelAtPeriodEnd,
      },
    })
  }

  async removeSubscriptionDiscount(programId: string, boxId: string) {
    await this.api.delete(`${this.buildUrl(programId)}/${boxId}/coupons`)
  }

  async applySubscriptionDiscount(programId: string, boxId: string, coupon: CouponInput) {
    await this.api.post(`${this.buildUrl(programId)}/${boxId}/coupons`, coupon)
  }

  async searchProgramSubscribersOnlyGyms(
    programId: string,
    page = 0,
    size = 30,
    searchTerm?: string,
  ): Promise<Page<ProgramSubscriberDto>> {
    const result = await this.api.get<PaginatedWrapper<ProgramSubscriberDto>>(
      `${this.buildBaseUrl(programId)}/searchOnlyGyms`,
      {
        params: {
          boxName: searchTerm,
          page,
          size,
        },
      },
    )

    const pageResult = wrapperToPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content.map((subscriber) =>
        LoggerService.parseApiDto(ProgramSubscriberDtoSchema, subscriber),
      ),
    }
  }
}
